// Phase 117: frontend-local publicId format validator.
//
// We cannot directly import the runtime symbol `isValidPublicId` from
// `@hookmyapp/shared` because that package is emitted as CommonJS (module: commonjs
// target: ES2022) and transitively requires nanoid via `require(...)` -- Vite's
// optimizeDeps pre-bundle chokes on that chain ("Calling `require` for 'nanoid'
// in an environment that doesn't expose the `require` function").
//
// Branded TYPES (`WorkspacePublicId`, etc.) are import-type-only and work
// cleanly because TypeScript erases them at compile time -- no runtime module
// resolution occurs.
//
// The backend/worker side already validates `isValidPublicId` at every HTTP
// boundary (see backend/src/common/public-id/resolve-public-id.pipe.ts,
// workspace.guard.ts). This frontend helper is a pre-network format gate --
// primarily for localStorage stale-value detection -- so a tight 8-line
// implementation sharing the same regex as the shared package is sufficient.
// Keep in sync with packages/shared/src/publicId.ts PUBLIC_ID_LENGTH=8.

type PublicIdPrefix = 'ws' | 'ch' | 'usr' | 'inv' | 'ssn' | 'mem' | 'org';

const PUBLIC_ID_LENGTH = 8;

/**
 * True iff `value` is a string matching `^{prefix}_[0-9A-Za-z]{8}$`.
 * Rejects UUIDs, wrong prefixes, wrong lengths, dashes/underscores in body,
 * and non-string inputs. Mirrors `isValidPublicId` from `@hookmyapp/shared`.
 */
export function isValidPublicId(
  value: unknown,
  prefix: PublicIdPrefix,
): boolean {
  if (typeof value !== 'string') return false;
  const re = new RegExp(`^${prefix}_[0-9A-Za-z]{${PUBLIC_ID_LENGTH}}$`);
  return re.test(value);
}
